import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Avatar,
    Stack,
    Divider,
    Button,
    Collapse
} from '@mui/material';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

// Default Splide theme CSS
import '@splidejs/react-splide/css';

const testimonials = [
    {
        name: "Anusha Sundaramurthi",
        title: "Bachelor of Technology - Computer Science and Engineering (2022 - 2026)",
        content: [
            "I had the opportunity to learn under her guidance during my internship, and it was a highly valuable and technically enriching experience.She taught core concepts like NLP, LLMs, and data processing completely from scratch, focusing on strong fundamentals rather than just using pre-built tools. The learning was project-driven, where we built a real-world project and learned concepts step-by-step through implementation.",
            "I gained practical experience in text preprocessing (tokenization, normalization, vectorization), building NLP pipelines, and working with tasks like text classification and information extraction. I also developed an understanding of embeddings and how models interpret context and semantics.",
            "She was highly engaging and consistently ensured we truly understood each concept. Her teaching standard was on par with international-level training, with a strong focus on real-world application.",
            "Overall, her mentorship significantly strengthened my technical foundation and ability to build NLP/LLM-based solutions."
        ]
    },
    {
        name: "D. Harini",
        title: "Bachelor of Technology - Computer Science Engineering",
        content: [
            "I would like to sincerely thank you for the structured and insightful training. Throughout the sessions, I gained a strong foundational understanding of Natural Language Processing (NLP), starting from core concepts such as intent recognition and semantic search, and further explored advanced topics including vector embeddings and cosine similarity for measuring relationships between textual data.",
            "The learning journey also introduced me to modern AI concepts like Large Language Models (LLMs) and Retrieval-Augmented Generation (RAG), which gave me clarity on how intelligent systems are designed and optimized. Additionally, I gained practical exposure to deploying models using FastAPI, helping me connect theoretical knowledge with real-world applications.",
            "Overall, this training has significantly enhanced my technical understanding and confidence in the NLP domain, and I am truly grateful for your continuous support and guidance, which has inspired me to keep learning and growing.",
            "I had the privilege of learning under her mentorship during my internship, which significantly enhanced my technical understanding. Her teaching emphasized first-principles learning in NLP and LLMs, from text preprocessing (tokenization, normalization, vector representation) to understanding contextual and semantic relationships.",
            "The training was highly application-oriented, driven by real-world project implementation. This enabled hands-on experience in designing NLP workflows, including text classification and embedding-based analysis.",
            "Overall, her mentorship played a crucial role in strengthening my conceptual depth and practical skills in building AI-driven solutions."
        ]
    },
];

const TestimonialSection = () => {
    // State to track which testimonial is expanded
    const [expandedId, setExpandedId] = useState(null);

    const handleExpandClick = (index) => {
        setExpandedId(expandedId === index ? null : index);
    };

    return (
        <Box sx={{
            py: 10,
            bgcolor: 'transparent',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center'
        }}>
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography
                        variant="overline"
                        sx={{ fontWeight: 'bold', color: '#3f51b5', letterSpacing: 2 }}
                    >
                        Success Stories
                    </Typography>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 800,
                            mt: 1,
                            color: '#ffffff',
                            fontSize: { xs: '2.2rem', md: '3.5rem' }
                        }}
                    >
                        What My Mentees Say
                    </Typography>
                    <Box sx={{ width: 80, height: 5, bgcolor: '#3f51b5', mx: 'auto', mt: 2, borderRadius: 2 }} />
                </Box>

                <Splide
                    options={{
                        type: 'loop',
                        perPage: 1,
                        autoplay: !expandedId, // Pause autoplay when someone is reading
                        interval: 5000,
                        pauseOnHover: true,
                        arrows: true,
                        pagination: true,
                        gap: '2rem',
                    }}
                >
                    {testimonials.map((item, index) => {
                        const isExpanded = expandedId === index;

                        return (
                            <SplideSlide key={index}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        borderRadius: 8,
                                        bgcolor: '#f8f9fa',
                                        border: '1px solid #e0e0e0',
                                        mx: { xs: 1, md: 4 },
                                        mb: 6,
                                        transition: 'all 0.3s ease-in-out'
                                    }}
                                >
                                    <CardContent sx={{ p: { xs: 4, md: 7 } }}>
                                        <Stack
                                            direction={{ xs: 'column', md: 'row' }}
                                            spacing={5}
                                            alignItems={{ xs: 'center', md: 'flex-start' }}
                                        >
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Avatar
                                                    sx={{
                                                        width: 100, height: 100,
                                                        bgcolor: '#1a237e', fontSize: '2rem',
                                                        fontWeight: 'bold', mb: 2,
                                                        border: '4px solid white',
                                                        boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                                    }}
                                                >
                                                    {item.name.charAt(0)}
                                                </Avatar>
                                                <FormatQuoteIcon sx={{ fontSize: 50, color: '#1a237e', opacity: 0.2 }} />
                                            </Box>

                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#000000', mb: 0.5 }}>
                                                    {item.name}
                                                </Typography>
                                                <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 600, mb: 3 }}>
                                                    {item.title}
                                                </Typography>

                                                <Divider sx={{ mb: 4, width: '60px', borderBottomWidth: 3, borderColor: '#1a237e' }} />

                                                {/* Preview Section: Shows only the first paragraph */}
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: '#000000',
                                                        lineHeight: 1.8,
                                                        fontSize: '1.05rem',
                                                        textAlign: 'justify',
                                                        fontWeight: 450
                                                    }}
                                                >
                                                    {item.content[0]}
                                                </Typography>

                                                {/* Collapsible Section: Shows the rest */}
                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    <Box sx={{ mt: 2.5 }}>
                                                        {item.content.slice(1).map((paragraph, pIndex) => (
                                                            <Typography
                                                                key={pIndex}
                                                                variant="body1"
                                                                sx={{
                                                                    mb: 2.5,
                                                                    color: '#000000',
                                                                    lineHeight: 1.8,
                                                                    fontSize: '1.05rem',
                                                                    textAlign: 'justify',
                                                                    fontWeight: 450
                                                                }}
                                                            >
                                                                {paragraph}
                                                            </Typography>
                                                        ))}
                                                    </Box>
                                                </Collapse>

                                                <Button
                                                    onClick={() => handleExpandClick(index)}
                                                    endIcon={isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                    sx={{
                                                        mt: 2,
                                                        color: '#1a237e',
                                                        fontWeight: 'bold',
                                                        textTransform: 'none',
                                                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                                                    }}
                                                >
                                                    {isExpanded ? "Read Less" : "Read More"}
                                                </Button>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </SplideSlide>
                        );
                    })}
                </Splide>
            </Container>
        </Box>
    );
};

export default TestimonialSection;